'use strict';
/* ==========================================================================
   levels/song.js — "Who Eats Whom?" step 4: THE SONG, "Follow the Arrows" (DESIGN.md §5.5; content/song.json),
   orientation "any". Model: lessons/blue-eyes/levels/song.js and lessons/circles/levels/jingle.js (same controls).

   ► The song: assets/music/song.m4a (the one ACE-Step take; lesson.json preload.stream: played through the shared
     <audio> element, never decoded) + assets/music/song_words.json (tools/song_timing.py: the TRUE lyric lines with word
     times, sections, the beat grid). The karaoke shows ONLY the words of song_words.json. Nothing here hard-codes a
     time: every cue is found by the lines' SECTIONS and sung words (S.section, S.W), the even arrow pace by the beat grid.
   ► Without the timings (missing / broken file): the lyrics of content/song.json advance line by line, evenly over the
     sheet's duration (S.even = true), with the same picture, controls and stamp; the real timings take over by
     themselves the next time the step opens with the file present.

   The picture is a pure function of SONG TIME t (seeking, pausing and stills are exact): the 15-organism web of the
   model (WebKit.Board, layout 'web', on the cork) draws itself over the song. Every plate, arrow and colour comes from
   Web (core-local/web.js): nothing typed.
     intro      the producers pin on (Web.role)
     verse 1    the periphyton ringed on its word; the apple snail and the snail kite pin on their words and the two
                arrows of Web.chain('kite') draw on the two "eats"; the chain glows on "Energy"
     chorus 1 + verse 2   every other native feeding link (Web.links('feed')) draws on, one per bar (the beat grid),
                bottom row first; each eater pins a beat before its first arrow
     bridge     the decomposers pin on "Decomposers" and the dead-matter links (Web.links('dead'), dashed) fan in; they
                lift off again on "Change", where the python pins on and its arrows (rust) draw on over the next lines
     chorus 2 + outro     the whole web with the python, energy dots running (on song time); FIELD-CHECKED stamps in
                after the last chorus line
   Karaoke (the band at the bottom): the current line, the sung word under a highlighter, the hook words (content/song.json
   "hook", first two lines, in the choruses) in red pen, the next line faint; instrumental gaps: three ink notes on the beat.
   Controls: click / Space play-pause, ↺ restart (R), ⌂ map, ← → seek 5 s, click or drag the strip at the bottom.
   "Finish" once ≥ 90 % of the song has been heard; the end of a listen with ≥ 90 % heard stamps the step.
   Portrait phone: the picture on the stage, Dock.karaoke + ↺ ▶ ⌂ + the strip below.

   QA hooks: sc.song (S, E, W, section, items, even), sc.plan, sc.debugAt(t), sc.now(), sc.play(t), sc.pause(), sc.toggle(),
   sc.seek(t), sc.restart(), sc.done(), sc.finish(), sc.canFinish(), sc.lyricAt(t), sc.activeWord(t), sc.pictureAt(t),
   sc.heard, sc.ended, sc.audioReady, sc.thumb (gen_thumb: the picture only, full stage); Levels.song.Song / plan / evenSong.
   ========================================================================== */
(function () {
  const STEP = 'song';
  const AUDIO = 'music/song.m4a', WORDS = 'music/song_words.json';
  const KEY = 'song:food-web';
  const BAND_Y = 846, CUR_Y = 930, NEXT_Y = 1016, CUR_SIZE = 60, NEXT_SIZE = 36;
  const THRESH = 0.9;
  const CREDIT = 'AI-generated song · the science is real';
  const NEVER = -1e9;
  const K = () => window.WebKit, W = () => window.Web;
  const cl = (x, a = 0, b = 1) => Math.max(a, Math.min(b, x));

  /* ---------------- song timing ---------------- */
  const normW = w => String(w).toLowerCase().replace(/[^a-z0-9]/g, '');
  class Song {
    constructor(d) {
      this.d = d; this.lines = d.lines.filter(l => l.words && l.words.length); this.dur = d.duration || (this.lines.length ? this.lines[this.lines.length - 1].e + 2 : 80);
      this.even = !!d.even;
      const b = d.beats || {};
      this.bpm = b.bpm || 100; this.bt = 60 / this.bpm; this.off = b.offset || 0; this.bar = 4 * this.bt;
      this.items = this.lines.map(l => ({ ...l, kind: 'line' })).concat((d.extras || []).filter(x => x.words && x.words.length).map((x, j) => ({ ...x, i: 100 + j }))).sort((a, c) => a.s - c.s);
      this.sec = [];
      this.lines.forEach((l, i) => { const last = this.sec[this.sec.length - 1]; if (last && last.name === l.section) last.lines.push(i); else this.sec.push({ name: l.section, lines: [i] }); });
    }
    section(name, n = 1) { const s = this.sec.filter(x => x.name === name)[n - 1]; return s ? s.lines : []; }
    S(i) { return this.lines[i] ? this.lines[i].s : 1e6; }
    E(i) { return this.lines[i] ? this.lines[i].e : 1e6; }
    /* start of the n-th word of line i that matches `word` (exact, then prefix); the line start if none */
    W(i, word, n = 1) {
      const l = this.lines[i]; if (!l) { if (window.__songCueMiss) window.__songCueMiss.push('?' + i + ':' + word); return 1e6; }
      const k = normW(word);
      for (const pass of [w => normW(w.w) === k, w => normW(w.w).startsWith(k)]) {
        let c = 0;
        for (const w of l.words) if (pass(w) && ++c === n) return w.s;
      }
      if (window.__songCueMiss) window.__songCueMiss.push(i + ':' + word);
      return l.s;
    }
    beatPos(t) { return (t - this.off) / this.bt; }
    snap(t) { return this.off + Math.round((t - this.off) / this.bt) * this.bt; }
    sinceBeat(t) { const p = this.beatPos(t); return (p - Math.floor(p)) * this.bt; }
    pulse(t, k = 9) { return t < this.off ? 0 : Math.exp(-this.sinceBeat(t) * k); }
  }
  /* no timings: the sheet's lines, evenly over its duration (words evenly inside each line) */
  function evenSong(sheet) {
    const dur = (sheet && sheet.duration) || 80, out = [];
    let sec = '';
    for (const raw of String((sheet && sheet.lyrics) || '').split('\n')) {
      const l = raw.trim(); if (!l) continue;
      const m = /^\[(.+)\]$/.exec(l);
      if (m) sec = m[1]; else out.push({ section: sec, text: l });
    }
    const t0 = 1.5, slot = (dur - 3) / Math.max(1, out.length);
    const lines = out.map((l, i) => {
      const s = t0 + i * slot, e = s + slot * 0.85, ws = l.text.split(/\s+/);
      return { i, section: l.section, text: l.text, s, e, words: ws.map((w, j) => ({ w, s: s + (e - s) * j / ws.length, e: s + (e - s) * (j + 1) / ws.length })) };
    });
    return { duration: dur, lines, extras: [], beats: { bpm: (sheet && sheet.bpm) || 100, offset: 0 }, even: true };
  }

  /* ---------------- the plan: when each plate pins and each arrow draws (song time) ---------------- */
  function plan(S) {
    const Wm = W(), LAY = K().LAYOUT, feed = Wm.links('feed');
    const isPy = l => l.from === 'python' || l.to === 'python';
    const pins = {}, arrows = [], hi = [];
    const pin = (id, t) => { if (pins[id] == null || t < pins[id]) pins[id] = t; };
    const v1 = S.section('Verse 1'), c1 = S.section('Chorus', 1), v2 = S.section('Verse 2'), br = S.section('Bridge'), c2 = S.section('Chorus', 2), ou = S.section('Outro');
    // intro: the producers
    Wm.natives().filter(id => Wm.role(id) === 'producer').forEach((id, i) => pin(id, 0.4 + i * 0.35));
    // verse 1: the kite chain on its words
    const ch = Wm.chain('kite');
    const lP = v1[0], lS = v1[2], lE = v1[3];
    if (v1.length >= 4) {
      hi.push({ ids: [ch[0]], t0: S.W(lP, 'Periphyton'), t1: S.S(lS) - 0.2 });
      pin(ch[1], S.W(lS, 'snail') - 0.1);
      arrows.push({ from: ch[0], to: ch[1], t: S.W(lS, 'eats', 1) });
      pin(ch[2], S.W(lS, 'kite') - 0.1);
      arrows.push({ from: ch[1], to: ch[2], t: S.W(lS, 'eats', 2) });
      hi.push({ ids: ch.slice(), t0: S.W(lE, 'Energy'), t1: c1.length ? S.S(c1[0]) + 1.2 : S.E(lE) + 2 });
    } else {
      pin(ch[1], 4); pin(ch[2], 5); arrows.push({ from: ch[0], to: ch[1], t: 4.5 }, { from: ch[1], to: ch[2], t: 5.5 });
    }
    // chorus 1 + verse 2: every other native feeding link, one per bar (bottom row first)
    const inChain = l => ch.some((id, k) => k && ch[k - 1] === l.from && id === l.to);
    const rest = feed.filter(l => !isPy(l) && !inChain(l)).sort((a, b) => (LAY[b.to][1] - LAY[a.to][1]) || (LAY[b.from][1] - LAY[a.from][1]) || (LAY[a.to][0] - LAY[b.to][0]) || (LAY[a.from][0] - LAY[b.from][0]));
    const tA = c1.length ? S.S(c1[0]) : 24, tB = v2.length ? S.E(v2[v2.length - 1]) : tA + 26;
    rest.forEach((l, k) => {
      const t = S.snap(tA + (k + 0.5) * (tB - tA) / rest.length);
      pin(l.from, t - 2 * S.bt); pin(l.to, t - S.bt);
      arrows.push({ from: l.from, to: l.to, t });
    });
    // the bridge: the decomposers and the dead-matter links, then the python and its arrows
    const dec = Wm.data().dead.to;
    const tDec = br.length ? S.W(br[0], 'Decomposers') : tB + 2;
    const tPy = br.length > 2 ? S.W(br[2], 'Change') : tDec + 9;
    pin(dec, tDec - 0.1);
    const dead = Wm.links('dead').filter(l => !isPy(l));
    const dEnd = br.length ? Math.max(tDec + 2, S.E(br[0])) : tDec + 3;
    dead.forEach((l, k) => arrows.push({ from: l.from, to: l.to, kind: 'dead', t: tDec + 0.3 + k * (dEnd - tDec - 0.3) / dead.length, t1: tPy - 0.25 }));
    pin('python', tPy);
    const py = feed.filter(isPy);
    const pA = br.length > 2 ? S.W(br[2], 'whole') : tPy + 1, pB = br.length > 3 ? S.E(br[3]) : pA + 6;
    py.forEach((l, k) => arrows.push({ from: l.from, to: l.to, t: S.snap(pA + k * (pB - pA) / Math.max(1, py.length - 1)), style: { color: K().C.rust }, python: true }));
    // the whole web on the last chorus's "eater" lines: the kite chain glows once more
    if (c2.length) hi.push({ ids: ch.slice(), t0: S.S(c2[0]), t1: S.S(c2[0]) + 2 * S.bar });
    const tStamp = c2.length ? S.E(c2[c2.length - 1]) + 0.6 : S.dur - 6;
    const ticks = S.sec.slice(1).map(s => Math.max(0, S.S(s.lines[0]) - 0.3));
    void ou;
    return { pins, arrows, hi, tDec, tPy, tStamp, ticks, order: arrows.filter(a => a.kind !== 'dead').map(a => a.from + '>' + a.to) };
  }

  /* ---------------- karaoke ---------------- */
  let HOOKS = null;
  const hooks = () => HOOKS || (HOOKS = (((Game.content || {}).song || {}).hook || []).slice(0, 2).map(l => l.split(/\s+/).map(normW).filter(Boolean)));
  function hookIdx(item) {
    const ws = item.words, out = new Set();
    if (!/^Chorus/.test(item.section || '')) return out;
    for (const h of hooks()) for (let k = 0; k + h.length <= ws.length; k++) if (h.every((w, j) => normW(ws[k + j].w) === w)) h.forEach((_, j) => out.add(k + j));
    return out;
  }
  const fontOk = () => !document.fonts || document.fonts.status === 'loaded';
  const widths = new Map();
  function tw(str, font) {
    const k = font + '|' + str; let w = widths.get(k);
    if (w === undefined) { g.save(); g.font = font; w = g.measureText(str).width; g.restore(); if (fontOk()) { if (widths.size > 3000) widths.clear(); widths.set(k, w); } }
    return w;
  }
  const hand = size => `${size}px ${K().F.hand}`;
  const typeF = size => `${size}px ${K().F.type}`;
  const lays = new Map();
  function layout(item, size) {
    const key = item.i + '|' + item.s + '|' + size;
    if (lays.has(key)) return lays.get(key);
    const f = hand(size), sp = tw(' ', f), hk = hookIdx(item);
    let x = 0;
    const toks = item.words.map((w, j) => { const ww = tw(w.w, f), tk = { text: w.w, s: w.s, e: w.e, x, w: ww, hook: hk.has(j) }; x += ww + sp; return tk; });
    const out = { toks, width: x - sp, size };
    if (fontOk()) lays.set(key, out);
    return out;
  }
  const wordEnd = (ws, j) => { const nx = ws[j + 1]; return Math.min(Math.max(ws[j].e, ws[j].s + 0.18) + 0.05, nx ? nx.s - 0.02 : 1e9); };
  const appearAt = (it, k) => (k === 0 ? it[0].s - 0.5 : Math.min(it[k].s, Math.max(it[k].s - 0.5, it[k - 1].e + 0.05)));
  function lyricAt(song, t) {
    const it = song.items;
    let cur = -1;
    for (let k = 0; k < it.length; k++) if (t >= appearAt(it, k)) cur = k;
    let c = cur >= 0 ? it[cur] : null;
    if (c) c.appear = appearAt(it, cur);
    const nx = it[cur + 1] || null;
    if (c && t > c.e + 2.2 && (!nx || nx.s - t > 1.2)) c = null;       // a long instrumental gap: clear the line
    return { cur: c, next: nx && nx.s - t < 8 ? nx : null };
  }
  function activeWord(song, t) {
    const { cur } = lyricAt(song, t); if (!cur) return null;
    const ws = cur.words;
    for (let j = 0; j < ws.length; j++) if (t >= ws[j].s - 0.02 && t < wordEnd(ws, j)) return { line: cur.i, j, w: ws[j].w, s: ws[j].s, e: ws[j].e };
    return null;
  }
  function drawCurrent(item, t, y, maxW = 1700) {
    const C = K().C;
    let size = CUR_SIZE, Lo = layout(item, size);
    if (Lo.width > maxW) { size = Math.floor(size * maxW / Lo.width); Lo = layout(item, size); }
    const x0 = 960 - Lo.width / 2, appear = item.appear ?? item.s - 0.5, a = E.outCubic(cl((t - appear) / 0.25)), yy = y + (1 - a) * 30;
    const f = hand(size);
    Lo.toks.forEach((tk, j) => {
      const x = x0 + tk.x, sung = t >= tk.s, active = t >= tk.s - 0.02 && t < wordEnd(Lo.toks, j);
      if (active) { g.save(); g.fillStyle = 'rgba(242,193,74,0.55)'; g.fillRect(x - 8, yy - size * 0.38, tk.w + 16, size * 0.8); g.restore(); }
      g.save(); g.font = f; g.textAlign = 'left'; g.textBaseline = 'middle';
      if (!sung) { g.globalAlpha *= 0.32 * a; g.fillStyle = C.ink; g.fillText(tk.text, x, yy); }
      else {
        const p = E.outQuad(cl((t - tk.s) / cl(tk.e - tk.s, 0.08, 0.3)));
        if (p < 1) { g.save(); g.globalAlpha *= 0.32; g.fillStyle = C.ink; g.fillText(tk.text, x, yy); g.restore(); g.beginPath(); g.rect(x - 4, yy - size, (tk.w + 8) * p, size * 2); g.clip(); }
        g.fillStyle = tk.hook ? C.red : C.inkDark; g.fillText(tk.text, x, yy);
      }
      g.restore();
    });
  }
  /* an ink eighth note (drawn, not a font glyph) */
  function inkNote(x, y, s, dbl) {
    g.save(); g.translate(x, y); g.scale(s, s); g.fillStyle = K().C.ink; g.strokeStyle = K().C.ink; g.lineWidth = 4; g.lineCap = 'round';
    const heads = dbl ? [[-16, 14], [20, 6]] : [[0, 14]];
    heads.forEach(([hx, hy]) => { g.beginPath(); g.ellipse(hx, hy, 11, 8, -0.4, 0, TAU); g.fill(); g.beginPath(); g.moveTo(hx + 9, hy - 2); g.lineTo(hx + 9, hy - 46); g.stroke(); });
    g.lineWidth = 6; g.beginPath();
    if (dbl) { g.moveTo(heads[0][0] + 9, heads[0][1] - 46); g.lineTo(heads[1][0] + 9, heads[1][1] - 46); } else { g.moveTo(9, -32); g.quadraticCurveTo(26, -20, 22, -4); }
    g.stroke(); g.restore();
  }
  const endNote = () => (((Game.content || {}).song || {}).hook || []).slice(0, 2).join('. ') + '.';
  function band(song, t) {
    const C = K().C;
    paperRect(960, BAND_Y + 130, 2000, 280, C.label, { seed: 777, torn: true, depth: 1.6, boil: false });
    K().pin(140, BAND_Y + 8); K().pin(1780, BAND_Y + 8);
    const { cur, next } = lyricAt(song, t);
    const sec = (cur || next || {}).section;
    if (sec) { g.save(); g.font = typeF(26); g.fillStyle = C.pencil; g.textAlign = 'left'; g.textBaseline = 'middle'; g.fillText(sec.toLowerCase(), 70, BAND_Y + 34); g.restore(); }
    if (song.even) { g.save(); g.font = typeF(22); g.fillStyle = C.red; g.textAlign = 'right'; g.textBaseline = 'middle'; g.fillText('no timings: the lines move on evenly', 1850, BAND_Y + 34); g.restore(); }
    if (cur) drawCurrent(cur, t, CUR_Y);
    const nt = (txt, a) => { let size = NEXT_SIZE; const w = tw(txt, typeF(size)); if (w > 1700) size = Math.floor(size * 1700 / w);
      g.save(); g.globalAlpha *= a; g.font = typeF(size); g.fillStyle = C.pencil; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(txt, 960, NEXT_Y); g.restore(); };
    if (next) nt(next.text, 0.8);
    else if (song.items.length && t > song.items[song.items.length - 1].e) nt(endNote(), 0.9);
    if (!cur && !next && t > 0.6) {
      const b = ((Math.floor(song.beatPos(t)) % 3) + 3) % 3, pu = song.pulse(t, 6);
      for (let i = 0; i < 3; i++) inkNote(860 + i * 100, CUR_Y - 22 * pu * (i === b ? 1 : 0.15), 1, i === 1);
    }
  }

  /* ======================================================================
     the scene
     ====================================================================== */
  class SongScene extends Scene {
    enter() {
      this.song = null; this.plan = null; this.err = null; this.B = null;
      this.playing = false; this.frozen = false; this.ended = false; this.startedOnce = false;
      this.pos = 0; this.t0ctx = null; this.heard = 0; this.lastT = 0; this._lastDraw = null;
      this.audioReady = false; this.completed = false; this.gone = false;
      Audio.stopMusic();
      if (K()) K().load();
      const wurl = Assets.url(WORDS);
      const words = (window.Loader ? Loader.json(wurl).then(d => d || Promise.reject(new Error('no timings'))) : fetch(wurl).then(r => (r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status)))))
        .then(d => { if (!d || !Array.isArray(d.lines) || !d.lines.length) throw new Error('empty timings'); this.setSong(new Song(d)); })
        .catch(e => {
          this.err = String(e && e.message); console.warn('[song] no timings: the lines move on evenly', this.err);
          try { this.setSong(new Song(evenSong(Game.content && Game.content.song))); } catch (e2) { this.err += '; ' + e2.message; }
        });
      const audio = (Audio.has(KEY) ? Promise.resolve() : Audio.load({ [KEY]: Assets.url(AUDIO) })).then(() => { this.audioReady = Audio.has(KEY); }, e => { console.warn('[song] no audio', e && e.message); });
      Promise.all([words, audio]).then(() => { if (!this.gone && !this.frozen && !this.startedOnce && this.song) this.play(0); });
      if (window.Device) {
        this.onHidden = Device.on('hidden', () => { if (this.playing && !this.gone) { this.pause(); this.resumeOnReturn = true; } });
        this.onResume = Device.on('resume', () => { if (this.resumeOnReturn && !this.gone && Game.current() === this) { this.resumeOnReturn = false; this.play(this.pos); } });
      }
    }
    setSong(S) {
      this.song = S; this.plan = plan(S);
      const B = this.B = new (K().Board)({ layout: 'web', rect: () => this.rect(), hint: false });
      for (const [id, t0] of Object.entries(this.plan.pins)) B.show([id], t0, { stagger: 0 });
      for (const a of this.plan.arrows) if (a.kind !== 'dead') B.addArrow(a.from, a.to, { t0: a.t, dur: 0.5, style: a.style || null });
      this._dead = null; this._hi = null;
    }
    exit() {
      this.gone = true; Audio.stopGroup('song'); this.playing = false;
      if (this.media) this.media.pause();
      if (window.Device) { Device.off('hidden', this.onHidden); Device.off('resume', this.onResume); }
    }
    get key() { return KEY; }
    back() { Game.replace(new Levels.map()); }
    /* the board's rect: above the karaoke band, below the title; the whole stage in the dock / the thumb */
    rect() {
      if (this.thumb) return { x: 50, y: 178, w: 1820, h: 880 };
      if (UI.docked()) return { x: 40, y: 40, w: 1840, h: 1000 };
      return { x: 60, y: 160, w: 1800, h: 668 };
    }
    /* the song clock: the <audio> element while it plays (t0ctx != null = "on the audio clock"), else pos */
    now() {
      if (this.playing && this.t0ctx != null) {
        const m = this.media;
        if (m) return m.playing() ? m.time() : m.el && m.el.ended ? this.song.dur : this.pos;
        if (Audio.running()) return Audio.now() - this.t0ctx;
      }
      return this.pos;
    }
    play(from = this.now()) {
      if (!this.song) return;
      this.startedOnce = true; this.frozen = false; this.ended = false;
      from = cl(from, 0, this.song.dur - 0.05);
      Audio.stopGroup('song');
      this.pos = from; this.lastT = from; this.playing = true; this.t0ctx = null;
      if (!this.audioReady || window.__timeScale > 1) return;
      this.media = this.media || Audio.media(KEY);
      if (this.media) {
        const m = this.media, tok = this.playTok = (this.playTok || 0) + 1;
        this.playAt = Game.time; this.blocked = false;
        m.play(from).then(ok => {
          if (tok !== this.playTok || this.gone) return;
          if (ok && this.playing) { this.pos = m.time(); this.t0ctx = 0; } else if (!ok) { this.pause(); this.blocked = true; }
        });
      } else if (Audio.running()) {
        const at = Audio.now() + 0.06;
        Audio.schedule(KEY, { at, offset: from, gain: 1, group: 'song', bus: 'voice', fade: 0.03 });
        this.t0ctx = at - from;
      }
    }
    pause() { this.pos = this.now(); this.playing = false; this.t0ctx = null; this.playTok = (this.playTok || 0) + 1; Audio.stopGroup('song'); if (this.media) this.media.pause(); }
    toggle() { if (this.playing) this.pause(); else this.play(this.ended ? 0 : this.pos); Audio.play('paper', { gain: 0.25 }); }
    seek(t) { if (!this.song) return; const was = this.playing; this.pause(); this.pos = cl(t, 0, this.song.dur); this.lastT = this.pos; this.ended = false; if (was) this.play(this.pos); }
    restart() { this.heard = 0; this.play(0); }
    /* QA: freeze on song time t (no pause overlay) */
    debugAt(t) { this.pause(); this.frozen = true; this.startedOnce = true; this.ended = false; this.pos = t; this.lastT = t; }
    /* stamp the step now (the end of a ≥ 90 % listen, the "Finish" button, QA) */
    done() { if (this.completed) return; this.completed = true; this.pause(); if (!Game.progress.stamps[STEP]) Game.complete(STEP); }
    canFinish() { return !!this.song && !this.completed && this.heard >= THRESH * this.song.dur; }
    finish() { if (this.canFinish()) { Audio.play('stamp', { gain: 0.6 }); this.done(); } }
    lyricAt(t) { return this.song ? lyricAt(this.song, t) : null; }
    activeWord(t) { return this.song ? activeWord(this.song, t) : null; }
    /* what the picture shows at song time t (QA) */
    pictureAt(t) {
      const p = this.plan; if (!p) return null;
      const plates = Object.keys(p.pins).filter(id => p.pins[id] <= t).sort();
      const drawn = p.arrows.filter(a => a.t <= t && (a.t1 == null || t < a.t1));
      const h = p.hi.find(x => t >= x.t0 && t < x.t1);
      return { plates, feed: drawn.filter(a => a.kind !== 'dead').map(a => a.from + '>' + a.to), dead: drawn.filter(a => a.kind === 'dead').map(a => a.from + '>' + a.to),
        python: t >= p.pins.python, pyArrows: drawn.filter(a => a.python).map(a => a.from + '>' + a.to), stamp: t >= p.tStamp, hi: h ? h.ids.slice() : null };
    }

    update(dt) {
      if (!this.song || !this.playing || this.scrub) return;
      if (this.t0ctx == null) {
        this.pos += dt * (window.__timeScale || 1);
        const retry = this.audioReady && !(window.__timeScale > 1) && (this.media ? !this.blocked && Game.time - (this.playAt || 0) > 3 : Audio.running());
        if (retry) this.play(this.pos);
      }
      this.heard += dt * (this.t0ctx == null ? (window.__timeScale || 1) : 1);
      const now = this.now();
      if (this.lastT < this.plan.tStamp && now >= this.plan.tStamp) Audio.play('stamp', { gain: 0.6 });
      this.lastT = now;
      if (now >= this.song.dur - 0.05) {
        this.pause(); this.pos = this.song.dur; this.ended = true;
        if (!this.completed && this.heard >= THRESH * this.song.dur) this.done();
      }
    }
    keyDown(k) {
      if (!this.song) return;
      if (k === ' ') this.toggle();
      else if (k === 'r' || k === 'R') this.restart();
      else if (k === 'ArrowRight') this.seek(this.now() + 5);
      else if (k === 'ArrowLeft') this.seek(this.now() - 5);
      else if (k === 'Enter') { if (this.ended) this.back(); else this.finish(); }
    }
    stripZone() { return Math.max(32, UI.tap(32)); }
    pointerDown(x, y) {
      if (!this.song || this.ended) return;
      if (UI.docked()) { this.toggle(); return; }
      if (UI.inHud(x, y, 3)) return;
      if (this._finBox && Math.abs(x - this._finBox.x) < this._finBox.w / 2 && Math.abs(y - this._finBox.y) < this._finBox.h / 2) return;
      if (y > 1080 - this.stripZone()) {
        this.scrub = { was: this.playing };
        if (this.playing) this.pause();
        this.pos = cl(x / 1920 * this.song.dur, 0, this.song.dur - 0.05); this.lastT = this.pos; this.ended = false;
        return;
      }
      this.toggle();
    }
    pointerMove(x) { if (this.scrub && this.song) { this.pos = cl(x / 1920 * this.song.dur, 0, this.song.dur - 0.05); this.lastT = this.pos; } }
    pointerUp() {
      if (!this.scrub) return;
      const was = this.scrub.was; this.scrub = null; this.lastT = this.pos;
      if (was) this.play(this.pos);
    }

    /* ---------- the picture: the web drawing itself (a pure function of song time t) ---------- */
    sync(t) {
      const B = this.B, p = this.plan;
      // the dead-matter links are on the board from the bridge until the python arrives
      const deadOn = t < p.tPy - 0.25;
      if (this._dead !== deadOn) {
        this._dead = deadOn;
        for (const a of p.arrows) if (a.kind === 'dead') { if (deadOn) B.addArrow(a.from, a.to, { kind: 'dead', t0: a.t, dur: 0.35 }); else B.removeArrow(a.from, a.to); }
      }
      const h = p.hi.find(x => t >= x.t0 && t < x.t1) || null;
      if (this._hi !== h) { this._hi = h; B.highlight(h ? h.ids : null, h ? h.t0 : 0); }
      // a jump (seek, still, restart): the pins already passed make no sound
      if (!this.playing || this._lastDraw == null || Math.abs(t - this._lastDraw) > 0.5) for (const [id, v] of B.ids) if (v.t0 <= t + 0.02) B._sfx.add(id + v.t0);
      this._lastDraw = t;
    }
    picture(t) {
      const gT = T, gBF = BF, gTime = Game.time;
      T = t; BF = Math.floor(t * 8); Game.time = t;
      try {
        K().notebookGround();
        this.sync(t);
        const G = this.B.draw(this);
        // FIELD-CHECKED: in the free top-left corner of the cork (left of the top row, above the left of the second)
        if (t >= this.plan.tStamp - 0.01) {
          const a = G.plates.alligator, R = G.R, x1 = a ? a.x - a.w / 2 - 10 : R.x + 400, room = x1 - R.x - 16;
          const s = Math.min(this.thumb ? 0.85 : 0.75, room / 420), x = R.x + 8 + room / 2, y = a ? a.y - a.h * 0.12 : R.y + 80;
          K().stamp(this.stampText(), x, y, { t0: this.plan.tStamp, s, rot: -0.12 });
        }
        this._geo = G;
      } finally { T = gT; BF = gBF; Game.time = gTime; }
    }
    stampText() { return (Game.lesson && Game.lesson.completeCard && Game.lesson.completeCard.title) || 'FIELD-CHECKED'; }
    title() { return (Game.content && Game.content.song && Game.content.song.title) || 'Follow the Arrows'; }

    draw() {
      const docked = UI.docked(), title = this.title();
      if (!this.song) {
        K().notebookGround();
        K().text(title, 960, 380, 110, { color: K().C.ink });
        const msg = this.err ? 'the song is missing (' + this.err + ')' : 'tuning the guitar…';
        K().text(msg, 960, 520, K().fsz(40, 13), { face: K().F.type, color: K().C.pencil });
        if (docked) { Dock.head(title, ''); Dock.text('sg-wait', [msg], { cls: 'note center' }); this.dockBar(); } else this.chrome(null);
        return;
      }
      const t = cl(this.now(), 0, this.song.dur);
      this.picture(t);
      if (this.thumb) { this.thumbTitle(); return; }
      if (!docked) {
        const gT = T, gBF = BF; T = t; BF = Math.floor(t * 8);
        try { band(this.song, t); } finally { T = gT; BF = gBF; }
        this.chrome(t);
      } else this.dock(t, title);
    }
    thumbTitle() {
      const C = K().C, title = this.title();
      paperRect(560, 92, 980, 130, C.label, { seed: 791, depth: 1.4, boil: false, rot: -0.01 });
      K().pin(90, 46); K().pin(1030, 46);
      K().text(title, 560, 96, 96, { color: C.inkDark });
    }

    /* ---------- the portrait dock: karaoke, controls and the strip below the stage ---------- */
    dock(t, title) {
      const S = this.song, pl = this.plan;
      const mmss = s => Math.floor(s / 60) + ':' + String(Math.floor(s % 60)).padStart(2, '0');
      Dock.head(title, `${mmss(t)} / ${mmss(S.dur)}`);
      const { cur, next } = lyricAt(S, t);
      const sec = (cur || next || {}).section;
      const lastE = S.items.length ? S.items[S.items.length - 1].e : 0;
      if (cur) {
        const key = cur.i + ':' + cur.s;
        if (!this._dk || this._dk.key !== key) { const hk = hookIdx(cur); this._dk = { key, words: cur.words.map((w, j) => ({ w: w.w, s: w.s, e: w.e, hook: hk.has(j) })) }; }
        Dock.karaoke({ key, words: this._dk.words, t, next: next ? next.text : '', section: sec ? sec.toLowerCase() : '' });
      } else Dock.karaoke({ key: 'gap', words: [], t, next: next ? next.text : '', section: sec ? sec.toLowerCase() : '',
        note: t > lastE && S.items.length ? endNote() : this.startedOnce || this.frozen ? '♪ ♫ ♪' : 'loading the song…' });
      Dock.text('credit', ['♪ ' + CREDIT + (S.even ? ' · no timings: the lines move on evenly' : '')], { cls: 'small center' });
      if (this.ended && Game.current() === this) {
        Dock.buttons('sg-end', [{ label: 'Play it again', onClick: () => this.restart() }, { label: 'Back to the map', color: K().C.green, onClick: () => this.back() }]);
      } else if (this.canFinish() && Game.current() === this) {
        Dock.buttons('sg-fin', [{ label: 'Finish', color: K().C.red, onClick: () => this.finish() }]);
      }
      Dock.strip({ value: t, total: S.dur, ticks: pl.ticks, label: 'song position', onSeek: (v, phase) => this.scrubTo(v, phase) });
      this.dockBar();
      if (!this.playing && !this.frozen && this.startedOnce && !this.ended && !this.scrub) this.pausedMark(540, 150, 1080);
    }
    dockBar() {
      if (this.song && !this.ended) {
        Dock.button({ id: 'restart', glyph: '↺', label: 'restart', aria: 'play the song from the start', onClick: () => this.restart() });
        Dock.button({ id: 'play', glyph: this.playing ? '❚❚' : '▶', label: this.playing ? 'pause' : 'play', aria: this.playing ? 'pause' : 'play', primary: true, onClick: () => this.toggle() });
      }
      Dock.button({ id: 'map', glyph: '⌂', label: 'map', aria: 'back to the map', onClick: () => this.back() });
    }
    scrubTo(v, phase) {
      if (!this.song) return;
      const pos = cl(v, 0, this.song.dur - 0.05);
      if (phase === 'start') { this.scrub = { was: this.playing || (this.scrub && this.scrub.was) }; if (this.playing) this.pause(); this.ended = false; }
      this.pos = pos; this.lastT = pos;
      if (phase === 'end') { const was = this.scrub ? this.scrub.was : false; this.scrub = null; if (was) this.play(this.pos); }
    }
    /* the paused mark: a sepia wash and a gold paper disc with a drawn triangle */
    pausedMark(y, r, h) {
      g.save(); g.fillStyle = 'rgba(34,24,15,0.26)'; g.fillRect(0, 0, 1920, h); g.restore();
      paperCircle(960, y, r, K().C.gold, { seed: 63, depth: 1.6, boil: false });
      g.save(); g.fillStyle = K().C.inkDark; g.beginPath(); g.moveTo(960 - r * 0.32, y - r * 0.55); g.lineTo(960 + r * 0.55, y); g.lineTo(960 - r * 0.32, y + r * 0.55); g.closePath(); g.fill(); g.restore();
    }

    chrome(t) {
      const S = this.song, C = K().C, title = this.title();
      // the title on a pinned label, the credit under it
      const tf = 54, tw0 = K().measure(title, hand(tf)) + 60;
      paperRect(40 + tw0 / 2, 66, Math.round(tw0), 84, C.label, { seed: 781, depth: 1, boil: false, rot: -0.008 });
      K().pin(40 + tw0 / 2 + 6, 30);
      K().text(title, 40 + tw0 / 2, 68, tf, { color: C.inkDark });
      K().text(CREDIT, 44, 132, K().fsz(26, 11), { face: K().F.type, color: C.pencil, align: 'left' });
      const m = UI.hudSlot(0), r = UI.hudSlot(1), pl = UI.hudSlot(2), fx = { r: m.r, fixed: true };
      if (UI.iconButton('sg-map', '⌂', m.x, m.y, fx)) { this.back(); return; }
      if (S) {
        if (UI.iconButton('sg-restart', '↺', r.x, r.y, fx)) this.restart();
        if (UI.iconButton('sg-play', this.playing ? '❚❚' : '▶', pl.x, pl.y, { ...fx, color: C.gold })) this.toggle();
      }
      if (t == null) return;
      const zone = this.stripZone(), touch = zone > 32;
      if (!this.ended) UI.target('sg-strip', 960, 1080 - zone / 2, 1920, zone);
      const sh = touch ? 20 : 12, sy = 1080 - sh;
      g.save();
      g.fillStyle = 'rgba(58,42,28,0.25)'; g.fillRect(0, sy, 1920, sh);
      g.fillStyle = C.green; g.fillRect(0, sy, 1920 * t / S.dur, sh);
      g.fillStyle = C.rust; this.plan.ticks.forEach(b => g.fillRect(1920 * b / S.dur - 2, sy - 6, 4, sh + 6));
      g.restore();
      if (touch || this.scrub) paperCircle(cl(1920 * t / S.dur, 26, 1894), touch ? 1080 - 24 : sy + sh / 2, touch ? 22 : 14, C.green, { seed: 66, depth: 0.8, boil: false });
      if (!this.playing && !this.frozen && this.startedOnce && !this.ended) {
        this.pausedMark(430, 100, BAND_Y);
        K().text(UI.hint('paused · click or Space to play', 'paused · tap to play'), 960, 590, K().fsz(48, 14), { color: C.cream });
      }
      if (!this.startedOnce && !this.frozen) K().text('loading the song…', 960, 590, K().fsz(48, 14), { color: C.pencil });
      this._finBox = null;
      if (this.ended && Game.current() === this) {
        if (UI.button('sg-again', 'Play it again', 700, 560, { w: 440, h: 110, size: 50, color: C.gold })) this.restart();
        if (UI.button('sg-back', 'Back to the map', 1220, 560, { w: 440, h: 110, size: 50, color: C.green })) this.back();
      } else if (this.canFinish() && Game.current() === this) {
        const w = UI.tap(250), h = UI.tap(84), fy = m.y + m.r + 30 + h / 2, fxp = m.x + m.r - w / 2;
        this._finBox = { x: fxp, y: fy, w, h };
        if (UI.button('sg-finish', 'Finish', fxp, fy, { w, h, size: UI.text(40), color: C.red })) { this.finish(); return; }
      }
    }
  }
  SongScene.STEP = STEP;
  SongScene.KEY = KEY;
  SongScene.Song = Song;
  SongScene.plan = plan;
  SongScene.evenSong = evenSong;
  SongScene.hookIdx = hookIdx;
  Levels.song = SongScene;
})();
